import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, X, AlertCircle, TrendingUp, TrendingDown, Users, DollarSign, Activity, Wallet, Settings, Pin, PinOff } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { supabase } from '../lib/supabaseClient';
import { useNavigate } from 'react-router-dom';

const inputCls = "w-full px-4 py-2.5 bg-[#0a0b0d] border border-[rgba(255,255,255,0.1)] rounded-xl text-white text-sm placeholder-[#444] focus:outline-none focus:border-[#38FE60] focus:ring-1 focus:ring-[#38FE60]/20 transition-all";
const selectCls = "w-full px-4 py-2.5 bg-[#0a0b0d] border border-[rgba(255,255,255,0.1)] rounded-xl text-white text-sm focus:outline-none focus:border-[#38FE60] focus:ring-1 focus:ring-[#38FE60]/20 transition-all";

const SuperAdmin = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [accountBalances, setAccountBalances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBalances, setLoadingBalances] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closingPosition, setClosingPosition] = useState(false);
  const [showCloseAllDialog, setShowCloseAllDialog] = useState(false);
  const [closingAllPositions, setClosingAllPositions] = useState(false);
  const [showOpenAllDialog, setShowOpenAllDialog] = useState(false);
  const [openingAllPositions, setOpeningAllPositions] = useState(false);
  const [showOpenUserDialog, setShowOpenUserDialog] = useState(false);
  const [openingUserPosition, setOpeningUserPosition] = useState(false);
  const [selectedOpenUser, setSelectedOpenUser] = useState(null);
  const [openFormData, setOpenFormData] = useState({
    strikePrice: '',
    entryDate: '',
    expiry: '',
    optionType: 'C'
  });
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [error, setError] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [hoveredBalanceIdx, setHoveredBalanceIdx] = useState(null);
  const [pinnedAccounts, setPinnedAccounts] = useState([]);
  const [pinnedUsers, setPinnedUsers] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sa_pinnedUsers') || '[]'); } catch { return []; }
  });
  const [currencyDisplay, setCurrencyDisplay] = useState('USD');
  const [debugInfo, setDebugInfo] = useState({
    rawResponse: null,
    parsedData: null,
    dataLength: 0,
    processedUsers: [],
    errors: []
  });


  const fetchPositionsFromWebhook = async () => {
    const debugErrors = [];
    try {

      const response = await fetch('https://n8n.srv1103655.hstgr.cloud/webhook/d2d76a5e-3873-4478-959f-ef5bc7c513f7', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        const error = `Webhook error: ${response.status}`;
        debugErrors.push(error);
        throw new Error(error);
      }

      const contentType = response.headers.get('content-type');
      console.log('Response content-type:', contentType);

      const text = await response.text();
      console.log('Raw response text:', text);

      setDebugInfo(prev => ({
        ...prev,
        rawResponse: text,
        errors: debugErrors
      }));


      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        debugErrors.push(`JSON parse error: ${parseError.message}`);
        setDebugInfo(prev => ({ ...prev, errors: debugErrors }));
        console.error('Failed to parse response as JSON:', parseError);
        throw new Error('Invalid JSON response from webhook');
      }

      console.log('Webhook response:', data);
      console.log('Webhook response type:', typeof data);
      console.log('Is array:', Array.isArray(data));

      return data;
    } catch (error) {
      debugErrors.push(`Fetch error: ${error.message}`);
      setDebugInfo(prev => ({ ...prev, errors: debugErrors }));
      console.error('Error fetching positions from webhook:', error);
      throw error;
    }
  };


  const fetchAllPositions = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    if (!silent) setError(null);
    try {

      const webhookData = await fetchPositionsFromWebhook();
      console.log('Raw webhook data:', webhookData);


      if (!Array.isArray(webhookData)) {
        console.error('Webhook response is not an array:', webhookData);
        console.error('Type:', typeof webhookData);
        console.error('Keys:', Object.keys(webhookData || {}));


        if (webhookData && typeof webhookData === 'object' && webhookData.data && Array.isArray(webhookData.data)) {
          console.log('Found data property, using that instead');
          webhookData = webhookData.data;
        } else {
          setUsers([]);
          setLastUpdate(new Date());
          return;
        }
      }

      console.log('Processing webhook data array, length:', webhookData.length);
      console.log('Full webhook data:', JSON.stringify(webhookData, null, 2));

      setDebugInfo(prev => ({
        ...prev,
        parsedData: webhookData,
        dataLength: webhookData.length
      }));

      const usersWithPositions = webhookData.map((userResponse, index) => {
        const userName = userResponse.name || `User ${index + 1}`;
        let positions = [];
        let userError = null;

        console.log(`\n===== Processing user ${userName} =====`);
        console.log('Full userResponse object:', JSON.stringify(userResponse, null, 2));
        console.log('Available keys in userResponse:', Object.keys(userResponse));


        const apiKey = userResponse['API Key'] || userResponse.api_key || userResponse.apiKey || null;
        const apiSecret = userResponse['API Secret'] || userResponse.api_secret || userResponse.apiSecret || null;


        const currentBalance = userResponse.curr_balance || userResponse.current_balance ||
                              userResponse.balance || userResponse.Balance ||
                              userResponse['Current Balance'] || 0;

        console.log(`Extracted API Key for ${userName}:`, apiKey ? `Found (${apiKey.substring(0, 10)}...)` : 'NOT FOUND');
        console.log(`Extracted API Secret for ${userName}:`, apiSecret ? `Found (${apiSecret.substring(0, 10)}...)` : 'NOT FOUND');
        console.log(`Extracted Current Balance for ${userName}:`, currentBalance);

        try {

          if (userResponse.result === null || userResponse.result === undefined) {
            userError = userResponse.error || 'API credentials error or network issue';
            console.log(`User ${userName} has error:`, userError);
          }

          else if (typeof userResponse.result === 'string') {
            console.log(`User ${userName} result is string:`, userResponse.result);
            if (userResponse.result === '[]' || userResponse.result === '') {

              positions = [];
              console.log(`User ${userName} has no positions (empty array)`);
            } else {
              try {
                const parsedResult = JSON.parse(userResponse.result);
                console.log(`User ${userName} parsed result:`, parsedResult);
                if (Array.isArray(parsedResult)) {
                  positions = parsedResult.map((position) => {
                    const entryPrice = parseFloat(position.entry_price || 0);
                    const currentPrice = parseFloat(position.mark_price || position.entry_price || 0);
                    const rawSize = parseFloat(position.size || 0);
                    const contractValue = parseFloat(position.contract_value || position.product?.contract_value || 0);



                    const uplBid = (currentPrice - entryPrice) * contractValue * rawSize;


                    const percentageGrowth = entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

                    return {
                      id: position.product_id || `pos_${Date.now()}_${Math.random()}`,
                      productId: position.product_id,
                      symbol: position.product_symbol || position.product?.symbol || 'UNKNOWN',
                      side: rawSize > 0 ? 'long' : 'short',
                      size: Math.abs(rawSize),
                      entryPrice: entryPrice,
                      currentPrice: currentPrice,
                      contractValue: contractValue,
                      uplBid: uplBid,
                      percentageGrowth: percentageGrowth,
                      unrealizedPnl: parseFloat(position.unrealized_pnl || 0),
                      realizedPnl: parseFloat(position.realized_pnl || 0),
                      leverage: position.product?.default_leverage
                        ? parseInt(parseFloat(position.product.default_leverage))
                        : 1,
                      margin: parseFloat(position.margin || 0),
                      commission: parseFloat(position.commission || 0),
                      timestamp: position.created_at || new Date().toISOString()
                    };
                  });
                  console.log(`User ${userName} processed ${positions.length} positions from parsed string`);
                } else {
                  console.log(`User ${userName} parsed result is not an array:`, parsedResult);
                }
              } catch (parseError) {
                console.error(`Error parsing string result for ${userName}:`, parseError, userResponse.result);
                userError = 'Failed to parse position data';
              }
            }
          }

          else if (Array.isArray(userResponse.result)) {
            console.log(`User ${userName} result is array:`, userResponse.result);
            positions = userResponse.result.map((position) => {
              const entryPrice = parseFloat(position.entry_price || 0);
              const currentPrice = parseFloat(position.mark_price || position.entry_price || 0);
              const rawSize = parseFloat(position.size || 0);
              const contractValue = parseFloat(position.contract_value || position.product?.contract_value || 0);



              const uplBid = (currentPrice - entryPrice) * contractValue * rawSize;


              const percentageGrowth = entryPrice > 0 ? ((currentPrice - entryPrice) / entryPrice) * 100 : 0;

              return {
                id: position.product_id || `pos_${Date.now()}_${Math.random()}`,
                productId: position.product_id,
                symbol: position.product_symbol || position.product?.symbol || 'UNKNOWN',
                side: rawSize > 0 ? 'long' : 'short',
                size: Math.abs(rawSize),
                entryPrice: entryPrice,
                currentPrice: currentPrice,
                contractValue: contractValue,
                uplBid: uplBid,
                percentageGrowth: percentageGrowth,
                unrealizedPnl: parseFloat(position.unrealized_pnl || 0),
                realizedPnl: parseFloat(position.realized_pnl || 0),
                leverage: position.product?.default_leverage
                  ? parseInt(parseFloat(position.product.default_leverage))
                  : 1,
                margin: parseFloat(position.margin || 0),
                commission: parseFloat(position.commission || 0),
                timestamp: position.created_at || new Date().toISOString()
              };
            });
            console.log(`User ${userName} processed ${positions.length} positions from array`);
          }

          else {
            console.log(`User ${userName} result is unexpected type:`, typeof userResponse.result, userResponse.result);
            userError = 'Unexpected data format from API';
          }
        } catch (parseError) {
          console.error(`Error parsing positions for ${userName}:`, parseError);
          userError = 'Failed to parse position data';
        }


        const totalPnl = positions.reduce((sum, pos) => sum + (pos.uplBid || 0), 0);
        const totalMargin = positions.reduce((sum, pos) => sum + pos.margin, 0);

        const supabaseUserId =
          userResponse.user_id ||
          userResponse.userId ||
          userResponse.supabase_user_id ||
          userResponse.id ||
          null;

        const userObj = {
          id: `user_${index}_${userName.replace(/\s+/g, '_')}`,
          supabaseUserId,
          name: userName,
          email: userResponse.email || '',
          apiKey: apiKey,
          apiSecret: apiSecret,
          currentBalance: parseFloat(currentBalance) || 0,
          positions,
          totalPnl,
          totalMargin,
          positionCount: positions.length,
          status: userError ? 'error' : (positions.length > 0 ? 'active' : 'inactive'),
          error: userError,
          lastActive: new Date().toISOString()
        };

        console.log(`Final user object for ${userName}:`, {
          ...userObj,
          apiKey: userObj.apiKey ? 'Present' : 'Missing',
          apiSecret: userObj.apiSecret ? 'Present' : 'Missing',
          currentBalance: userObj.currentBalance
        });
        return userObj;
      });

      console.log('Processed users with positions:', usersWithPositions);
      console.log('Total users to display:', usersWithPositions.length);
      console.log('User names:', usersWithPositions.map(u => u.name));

      setDebugInfo(prev => ({
        ...prev,
        processedUsers: usersWithPositions.map(u => ({
          name: u.name,
          status: u.status,
          error: u.error,
          positionCount: u.positionCount,
          totalPnl: u.totalPnl
        }))
      }));

      setUsers(usersWithPositions);
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Error fetching positions:', error);
      if (!silent) setError('Failed to fetch user positions');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);


  const fetchAccountBalances = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingBalances(true);
    try {
      console.log('Fetching account balances from delta_users table...');

      const { data, error } = await supabase
        .from('delta_users')
        .select('id, name, curr_balance, inr, email')
        .order('name', { ascending: true });

      if (error) {
        console.error('Error fetching account balances:', error);
        throw error;
      }

      console.log('Account balances fetched:', data);

      // INR values are now fetched directly from Supabase
      const enhancedData = (data || []).map(account => ({
        ...account,
        inr_balance: parseFloat(account.inr || 0)
      }));

      setAccountBalances(enhancedData);

      if (data && data.length > 0) {
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.supabaseUserId) {
            return user;
          }

          const matchByEmail = user.email
            ? data.find(acc => acc.email && acc.email.toLowerCase() === user.email.toLowerCase())
            : null;
          const matchByName = data.find(acc => acc.name && acc.name === user.name);
          const matched = matchByEmail || matchByName;

          return matched
            ? { ...user, supabaseUserId: matched.id }
            : user;
        }));
      }
    } catch (error) {
      console.error('Failed to fetch account balances:', error);
    } finally {
      if (!silent) setLoadingBalances(false);
    }
  }, []);



  const openAllPositions = async () => {
    setOpeningAllPositions(true);
    try {
      console.log('===== STARTING OPEN ALL POSITIONS FLOW =====');
      console.log('Form data:', openFormData);


      if (!openFormData.strikePrice || !openFormData.entryDate || !openFormData.expiry || !openFormData.optionType) {
        alert('Please fill in all required fields (Strike Price, Entry Date, Expiry, Option Type)');
        setOpeningAllPositions(false);
        return;
      }


      const payload = {
        strike_price: parseFloat(openFormData.strikePrice),
        entry_date: openFormData.entryDate,
        expiry: openFormData.expiry,
        option_type: openFormData.optionType
      };

      console.log('===== OPEN ALL POSITIONS WEBHOOK CALL =====');
      console.log('Webhook URL:', 'https://n8n.srv1103655.hstgr.cloud/webhook/4d458aac-16b0-4797-b281-7012a6366619');
      console.log('Method: POST');
      console.log('Payload:', payload);


      const response = await fetch('https://n8n.srv1103655.hstgr.cloud/webhook/4d458aac-16b0-4797-b281-7012a6366619', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response received:');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('n8n webhook error response:', errorText);
        console.log('===== OPEN ALL POSITIONS FAILED =====');
        throw new Error(`Webhook request failed: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Parsed response JSON:', result);
      } catch (parseError) {
        console.log('Response is not JSON:', responseText);
        result = { raw: responseText };
      }

      console.log('===== OPEN ALL POSITIONS SUCCESS =====');

      setShowOpenAllDialog(false);
      setOpenFormData({
        strikePrice: '',
        entryDate: '',
        expiry: '',
        optionType: 'C'
      });

      console.log('Open all positions request completed successfully');
      console.log('===== OPEN ALL POSITIONS FLOW COMPLETE =====');
      alert(`Successfully sent open positions request with Strike Price: ${openFormData.strikePrice}, Entry Date: ${openFormData.entryDate}, Expiry: ${openFormData.expiry}, Type: ${openFormData.optionType}`);
    } catch (error) {
      console.error('===== OPEN ALL POSITIONS ERROR =====');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Failed to open all positions: ${error.message}`);
    } finally {
      setOpeningAllPositions(false);
    }
  };

  const openUserPosition = async () => {
    if (!selectedOpenUser) {
      alert('No user selected for opening a position');
      return;
    }

    const hasApiCredentials = !!(selectedOpenUser.apiKey && selectedOpenUser.apiSecret);
    if (!selectedOpenUser.supabaseUserId && !hasApiCredentials) {
      alert('Supabase user id or API credentials are required for this user');
      return;
    }

    setOpeningUserPosition(true);
    try {
      console.log('===== STARTING OPEN USER POSITION FLOW =====');
      console.log('Selected user:', selectedOpenUser);
      console.log('Form data:', openFormData);

      if (!openFormData.strikePrice || !openFormData.entryDate || !openFormData.expiry || !openFormData.optionType) {
        alert('Please fill in all required fields (Strike Price, Entry Date, Expiry, Option Type)');
        setOpeningUserPosition(false);
        return;
      }

      const payload = {
        user_id: selectedOpenUser.supabaseUserId || undefined,
        user_name: selectedOpenUser.name,
        api_key: selectedOpenUser.apiKey || undefined,
        api_secret: selectedOpenUser.apiSecret || undefined,
        strike_price: parseFloat(openFormData.strikePrice),
        entry_date: openFormData.entryDate,
        expiry: openFormData.expiry,
        exit_date: openFormData.expiry,
        option_type: openFormData.optionType
      };

      console.log('===== OPEN USER POSITION WEBHOOK CALL =====');
      console.log('Webhook URL:', 'https://n8n.srv1103655.hstgr.cloud/webhook/4d458aac-16b0-4797-b281-7012a6366619');
      console.log('Method: POST');
      console.log('Payload:', payload);

      const response = await fetch('https://n8n.srv1103655.hstgr.cloud/webhook/4d458aac-16b0-4797-b281-7012a6366619', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response received:');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('n8n webhook error response:', errorText);
        console.log('===== OPEN USER POSITION FAILED =====');
        throw new Error(`Webhook request failed: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Parsed response JSON:', result);
      } catch (parseError) {
        console.log('Response is not JSON:', responseText);
        result = { raw: responseText };
      }

      console.log('===== OPEN USER POSITION SUCCESS =====');

      setShowOpenUserDialog(false);
      setSelectedOpenUser(null);
      setOpenFormData({
        strikePrice: '',
        entryDate: '',
        expiry: '',
        optionType: 'C'
      });

      console.log('Open user position request completed successfully');
      console.log('===== OPEN USER POSITION FLOW COMPLETE =====');
      alert(`Open position request sent for ${selectedOpenUser.name}`);
    } catch (error) {
      console.error('===== OPEN USER POSITION ERROR =====');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Failed to open position for user: ${error.message}`);
    } finally {
      setOpeningUserPosition(false);
    }
  };


  const closeAllPositions = async () => {
    setClosingAllPositions(true);
    try {
      console.log('===== STARTING CLOSE ALL POSITIONS FLOW =====');


      console.log('Step 1: Fetching fresh data from n8n webhook...');
      const webhookData = await fetchPositionsFromWebhook();
      console.log('Received webhook data for', webhookData.length, 'users');


      console.log('Step 2: Collecting all positions from all users...');
      const allPositions = [];

      for (const user of users) {
        if (user.positions && user.positions.length > 0) {

          const userInWebhook = webhookData.find(u => u.name === user.name);

          if (!userInWebhook) {
            console.warn(`Skipping user ${user.name} - not found in webhook response`);
            continue;
          }


          const apiKey = userInWebhook['API Key'] || userInWebhook.api_key || userInWebhook.apiKey || null;
          const apiSecret = userInWebhook['API Secret'] || userInWebhook.api_secret || userInWebhook.apiSecret || null;

          if (!apiKey || !apiSecret) {
            console.warn(`Skipping user ${user.name} - missing API credentials`);
            continue;
          }


          for (const position of user.positions) {
            allPositions.push({
              api_key: apiKey,
              api_secret: apiSecret,
              product_id: position.productId,
              product_symbol: position.symbol,
              size: position.size,
              user_name: user.name
            });
          }
        }
      }

      console.log(`Collected ${allPositions.length} positions from ${users.length} users`);

      if (allPositions.length === 0) {
        alert('No open positions found to close');
        setClosingAllPositions(false);
        setShowCloseAllDialog(false);
        return;
      }


      console.log('Step 3: Preparing payload for close all positions webhook...');
      const payload = {
        positions: allPositions
      };

      console.log('===== CLOSE ALL POSITIONS WEBHOOK CALL =====');
      console.log('Sending', allPositions.length, 'positions to webhook');
      console.log('Webhook URL:', 'https://n8n.srv1103655.hstgr.cloud/webhook/ccbbd4cb-44cd-4aa1-9824-a1f9c4af98ea');
      console.log('Method: POST');
      console.log('Payload structure:', {
        positions: `Array[${allPositions.length}]`,
        sample: allPositions[0]
      });


      console.log('Step 4: Calling n8n close all positions webhook...');
      const response = await fetch('https://n8n.srv1103655.hstgr.cloud/webhook/ccbbd4cb-44cd-4aa1-9824-a1f9c4af98ea', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response received:');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('n8n webhook error response:', errorText);
        console.log('===== CLOSE ALL POSITIONS FAILED =====');
        throw new Error(`Webhook request failed: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Parsed response JSON:', result);
      } catch (parseError) {
        console.log('Response is not JSON:', responseText);
        result = { raw: responseText };
      }

      console.log('===== CLOSE ALL POSITIONS SUCCESS =====');


      console.log('Waiting 3 seconds before refreshing positions...');
      await new Promise(resolve => setTimeout(resolve, 3000));

      console.log('Refreshing positions...');
      await fetchAllPositions();

      setShowCloseAllDialog(false);

      console.log('All positions close request completed successfully');
      console.log('===== CLOSE ALL POSITIONS FLOW COMPLETE =====');
      alert(`Successfully sent close request for ${allPositions.length} positions from ${users.filter(u => u.positions?.length > 0).length} users`);
    } catch (error) {
      console.error('===== CLOSE ALL POSITIONS ERROR =====');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      alert(`Failed to close all positions: ${error.message}`);
    } finally {
      setClosingAllPositions(false);
    }
  };

  const closePosition = async (position, userId) => {
    setClosingPosition(true);
    try {
      console.log('===== STARTING CLOSE POSITION FLOW =====');
      console.log('Position to close:', {
        symbol: position.symbol,
        productId: position.productId,
        size: position.size,
        userName: position.userName
      });


      console.log('Step 1: Fetching fresh data from n8n webhook...');
      const webhookData = await fetchPositionsFromWebhook();
      console.log('Received webhook data for', webhookData.length, 'users');


      console.log('Step 2: Looking for user:', position.userName);
      const userInWebhook = webhookData.find(u => u.name === position.userName);

      if (!userInWebhook) {
        console.error('User not found in webhook response');
        console.log('Available users:', webhookData.map(u => u.name));
        throw new Error(`User "${position.userName}" not found in webhook response`);
      }

      console.log('Found user in webhook:', position.userName);
      console.log('Full user data from webhook:', JSON.stringify(userInWebhook, null, 2));
      console.log('Available keys:', Object.keys(userInWebhook));


      console.log('Step 3: Extracting API credentials...');
      const apiKey = userInWebhook['API Key'] || userInWebhook.api_key || userInWebhook.apiKey || null;
      const apiSecret = userInWebhook['API Secret'] || userInWebhook.api_secret || userInWebhook.apiSecret || null;

      console.log('API Key extracted:', apiKey ? `Found (${apiKey.substring(0, 15)}...)` : 'NOT FOUND');
      console.log('API Secret extracted:', apiSecret ? `Found (${apiSecret.substring(0, 15)}...)` : 'NOT FOUND');

      if (!apiKey || !apiSecret) {
        console.error('API credentials missing in webhook response');
        console.error('Tried fields: "API Key", "API Secret", api_key, apiKey');
        console.error('User object keys:', Object.keys(userInWebhook));
        console.error('Full user object:', userInWebhook);
        throw new Error('API credentials not found in webhook response. Please check n8n webhook configuration.');
      }


      console.log('Step 4: Preparing payload for close position webhook...');
      const payload = {
        api_key: apiKey,
        api_secret: apiSecret,
        product_id: position.productId,
        product_symbol: position.symbol,
        size: position.size
      };

      console.log('===== CLOSE POSITION WEBHOOK CALL =====');
      console.log('Payload:', {
        api_key: `${apiKey.substring(0, 15)}...`,
        api_secret: `${apiSecret.substring(0, 15)}...`,
        product_id: payload.product_id,
        product_symbol: payload.product_symbol,
        size: payload.size
      });
      console.log('Webhook URL:', 'https://n8n.srv1103655.hstgr.cloud/webhook-test/717a240d-df2a-4dc0-a2e6-4986bfa9deae');
      console.log('Method: POST');
      console.log('Headers:', { 'Content-Type': 'application/json', 'Accept': 'application/json' });


      console.log('Step 5: Calling n8n close position webhook...');
      const response = await fetch('https://n8n.srv1103655.hstgr.cloud/webhook/717a240d-df2a-4dc0-a2e6-4986bfa9deae', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      console.log('Response received:');
      console.log('Status:', response.status);
      console.log('Status Text:', response.statusText);
      console.log('Headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('n8n webhook error response:', errorText);
        console.log('===== CLOSE POSITION FAILED =====');
        throw new Error(`Webhook request failed: ${response.status} - ${errorText}`);
      }

      const responseText = await response.text();
      console.log('Raw response text:', responseText);

      let result;
      try {
        result = JSON.parse(responseText);
        console.log('Parsed response JSON:', result);
      } catch (parseError) {
        console.log('Response is not JSON:', responseText);
        result = { raw: responseText };
      }

      console.log('===== CLOSE POSITION SUCCESS =====');

      console.log('Waiting 2 seconds before refreshing positions...');
      await new Promise(resolve => setTimeout(resolve, 2000));

      console.log('Refreshing positions...');
      await fetchAllPositions();

      setShowCloseDialog(false);
      setSelectedPosition(null);

      console.log('Position close request completed successfully');
      console.log('===== CLOSE POSITION FLOW COMPLETE =====');
      alert(`Position close request sent successfully for ${position.symbol}`);
    } catch (error) {
      console.error('===== CLOSE POSITION ERROR =====');
      console.error('Error type:', error.name);
      console.error('Error message:', error.message);
      console.error('Error stack:', error.stack);
      console.error('Full error object:', error);
      alert(`Failed to close position: ${error.message}`);
    } finally {
      setClosingPosition(false);
    }
  };


  useEffect(() => {
    fetchAllPositions();
    fetchAccountBalances();

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchAllPositions({ silent: true });
        fetchAccountBalances({ silent: true });
      }, refreshInterval * 1000);

      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchAllPositions, fetchAccountBalances]);

  // Persist pinned users across refreshes
  useEffect(() => {
    localStorage.setItem('sa_pinnedUsers', JSON.stringify(pinnedUsers));
  }, [pinnedUsers]);

  const summary = {
    totalUsers: users.length,
    totalPositions: users.reduce((sum, user) => sum + user.positionCount, 0),
    totalPnl: users.reduce((sum, user) => sum + user.totalPnl, 0),
    totalMargin: users.reduce((sum, user) => sum + user.totalMargin, 0)
  };


  console.log('Rendering SuperAdmin with users:', users.length, users.map(u => u.name));

  const cardStyle = {
    background: 'linear-gradient(145deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderTop: '1px solid rgba(255,255,255,0.18)',
    borderRadius: '18px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)',
  };

  const formLabelCls = "block text-[#aaa] text-xs font-semibold uppercase tracking-widest mb-2";

  return (
    <div style={{ background: '#0a0b0d', fontFamily: "'Space Grotesk', sans-serif", paddingTop: '64px', paddingBottom: '64px', minHeight: '100vh' }} className="text-white">
      <style>{`@keyframes tooltipFadeIn { from { opacity:0; transform:translateX(-50%) translateY(4px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>

      {/* ── HEADER BAR ── */}
      <div
        style={{ background: 'rgba(8,10,12,0.85)', borderBottom: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)', boxShadow: '0 4px 24px rgba(0,0,0,0.3)', top: '64px' }}
        className="sticky z-40 px-8 py-4"
      >
        {/* Top row */}
        <div className="flex items-center justify-between gap-4 flex-wrap">
          {/* Left */}
          <div className="flex items-center gap-4">
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span style={{ color: '#38FE60', fontSize: '18px' }}>⬡</span>
                <span style={{ color: '#38FE60', fontSize: '11px', fontWeight: 700, letterSpacing: '0.15em' }} className="uppercase">Super Admin</span>
              </div>
              <h1 className="text-xl font-bold text-white leading-none">Trading Dashboard</h1>
              <p style={{ color: '#555', fontSize: '11px' }} className="mt-0.5">Real-time position monitoring</p>
            </div>
          </div>

          {/* Right */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-right mr-1">
              <p style={{ color: '#555', fontSize: '10px' }} className="uppercase tracking-widest">Last update</p>
              <p className="text-white text-sm font-semibold">{lastUpdate.toLocaleTimeString()}</p>
            </div>

            {/* Refresh */}
            <button
              onClick={fetchAllPositions}
              disabled={loading}
              style={{ background: '#1a4fd6', border: 'none', borderRadius: '10px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
              className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold hover:opacity-90 transition-opacity"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {/* Close All */}
            <button
              onClick={() => {
                const totalPositions = users.reduce((sum, user) => sum + (user.positions?.length || 0), 0);
                if (totalPositions === 0) { alert('No open positions to close'); return; }
                setShowCloseAllDialog(true);
              }}
              disabled={loading || users.length === 0}
              style={{ background: '#ff4d4d', border: 'none', borderRadius: '10px', cursor: (loading || users.length === 0) ? 'not-allowed' : 'pointer', opacity: (loading || users.length === 0) ? 0.5 : 1 }}
              className="flex items-center gap-2 px-4 py-2 text-white text-sm font-bold hover:opacity-90 transition-opacity"
            >
              <X className="w-4 h-4" />
              Close All
            </button>

            {/* Open All */}
            <button
              onClick={() => setShowOpenAllDialog(true)}
              disabled={loading || users.length === 0}
              style={{ background: '#38FE60', border: 'none', borderRadius: '10px', cursor: (loading || users.length === 0) ? 'not-allowed' : 'pointer', opacity: (loading || users.length === 0) ? 0.5 : 1 }}
              className="flex items-center gap-2 px-4 py-2 text-[#0a0b0d] text-sm font-bold hover:opacity-90 transition-opacity"
            >
              <TrendingUp className="w-4 h-4" />
              Open All
            </button>

            {/* Workflows */}
            <button
              onClick={() => navigate('/superadmin/workflows')}
              style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', cursor: 'pointer' }}
              className="flex items-center gap-2 px-4 py-2 text-[#ccc] text-sm font-semibold hover:bg-[rgba(255,255,255,0.1)] transition-colors"
            >
              <Settings className="w-4 h-4" />
              Workflows
            </button>
          </div>
        </div>

        {/* Bottom row — auto-refresh + debug toggles */}
        <div className="flex items-center gap-6 mt-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <div
              onClick={() => setAutoRefresh(v => !v)}
              style={{
                width: '36px', height: '20px', borderRadius: '10px',
                background: autoRefresh ? '#38FE60' : 'rgba(255,255,255,0.1)',
                position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0
              }}
            >
              <div style={{
                width: '14px', height: '14px', borderRadius: '50%', background: '#fff',
                position: 'absolute', top: '3px',
                left: autoRefresh ? '19px' : '3px', transition: 'left 0.2s'
              }} />
            </div>
            <span style={{ color: '#999', fontSize: '12px' }}>Auto-refresh</span>
          </label>

          {autoRefresh && (
            <div className="flex items-center gap-2">
              <span style={{ color: '#555', fontSize: '12px' }}>Interval:</span>
              <select
                value={refreshInterval}
                onChange={(e) => setRefreshInterval(Number(e.target.value))}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', color: '#ccc', fontSize: '12px', padding: '3px 8px' }}
              >
                <option value={5}>5s</option>
                <option value={10}>10s</option>
                <option value={30}>30s</option>
                <option value={60}>1 min</option>
              </select>
            </div>
          )}

          <button
            onClick={() => setShowDebug(!showDebug)}
            style={{ background: 'none', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', cursor: 'pointer', color: showDebug ? '#facc15' : '#555', fontSize: '12px', padding: '3px 12px' }}
            className="hover:border-[rgba(255,255,255,0.2)] transition-colors"
          >
            {showDebug ? 'Hide Debug' : 'Debug'}
          </button>
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-10" style={{ marginTop: '24px' }}>

        {/* ── STATS STRIP ── */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {/* Total Users */}
          <div
            style={{ ...cardStyle, borderLeft: '3px solid #3b82f6' }}
            className="p-6 group hover:shadow-[0_0_24px_rgba(59,130,246,0.12)] transition-all duration-300"
          >
            <div className="flex items-start justify-between">
              <div>
                <p style={{ color: '#555', fontSize: '11px', letterSpacing: '0.1em' }} className="uppercase font-semibold mb-2">Total Users</p>
                <p className="text-4xl font-bold text-white">{summary.totalUsers}</p>
              </div>
              <div style={{ background: 'rgba(59,130,246,0.12)', borderRadius: '12px', padding: '10px' }}>
                <Users style={{ color: '#3b82f6' }} className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Open Positions */}
          <div
            style={{ ...cardStyle, borderLeft: '3px solid #a855f7' }}
            className="p-6 group hover:shadow-[0_0_24px_rgba(168,85,247,0.12)] transition-all duration-300"
          >
            <div className="flex items-start justify-between">
              <div>
                <p style={{ color: '#555', fontSize: '11px', letterSpacing: '0.1em' }} className="uppercase font-semibold mb-2">Open Positions</p>
                <p className="text-4xl font-bold text-white">{summary.totalPositions}</p>
              </div>
              <div style={{ background: 'rgba(168,85,247,0.12)', borderRadius: '12px', padding: '10px' }}>
                <Activity style={{ color: '#a855f7' }} className="w-6 h-6" />
              </div>
            </div>
          </div>

          {/* Total P&L */}
          <div
            style={{ ...cardStyle, borderLeft: `3px solid ${summary.totalPnl >= 0 ? '#38FE60' : '#ff4d4d'}` }}
            className="p-6 group hover:shadow-[0_0_24px_rgba(56,254,96,0.1)] transition-all duration-300"
          >
            <div className="flex items-start justify-between">
              <div>
                <p style={{ color: '#555', fontSize: '11px', letterSpacing: '0.1em' }} className="uppercase font-semibold mb-2">Total P&L</p>
                <p className={`text-4xl font-bold ${summary.totalPnl >= 0 ? 'text-[#38FE60]' : 'text-[#ff4d4d]'}`}>
                  ${summary.totalPnl.toFixed(2)}
                </p>
              </div>
              <div style={{ background: summary.totalPnl >= 0 ? 'rgba(56,254,96,0.1)' : 'rgba(255,77,77,0.1)', borderRadius: '12px', padding: '10px' }}>
                {summary.totalPnl >= 0
                  ? <TrendingUp style={{ color: '#38FE60' }} className="w-6 h-6" />
                  : <TrendingDown style={{ color: '#ff4d4d' }} className="w-6 h-6" />}
              </div>
            </div>
          </div>

          {/* Total Margin */}
          <div
            style={{ ...cardStyle, borderLeft: '3px solid #facc15' }}
            className="p-6 group hover:shadow-[0_0_24px_rgba(250,204,21,0.12)] transition-all duration-300"
          >
            <div className="flex items-start justify-between">
              <div>
                <p style={{ color: '#555', fontSize: '11px', letterSpacing: '0.1em' }} className="uppercase font-semibold mb-2">Total Margin</p>
                <p className="text-4xl font-bold text-white">${summary.totalMargin.toFixed(2)}</p>
              </div>
              <div style={{ background: 'rgba(250,204,21,0.1)', borderRadius: '12px', padding: '10px' }}>
                <DollarSign style={{ color: '#facc15' }} className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>

        {/* ── TWO-COLUMN LAYOUT: balances left, positions right ── */}
        <div className="flex flex-col lg:flex-row gap-6 items-start">

        {/* ── ACCOUNT BALANCES (left column) ── */}
        <div style={{ flexShrink: 0 }} className="w-full lg:w-[280px]">

          {/* Glass panel wrapper */}
          <div style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 60%, rgba(255,255,255,0.04) 100%)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderTop: '1px solid rgba(255,255,255,0.18)',
            borderRadius: '20px',
            overflow: 'visible',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.1)',
            position: 'relative',
          }}>

            {/* Subtle top-left shine */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)', pointerEvents: 'none' }} />
            <div style={{ position: 'absolute', top: -60, left: -40, width: '180px', height: '180px', background: 'radial-gradient(circle, rgba(250,204,21,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

            {/* Header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '14px 16px 12px',
              borderBottom: '1px solid rgba(255,255,255,0.07)',
              background: 'rgba(255,255,255,0.02)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#facc15', boxShadow: '0 0 8px #facc15' }} />
                <span style={{ color: '#fff', fontSize: '13px', fontWeight: 700, letterSpacing: '-0.01em' }}>Account Balances</span>
                <span style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px', padding: '1px 7px', color: '#888', fontSize: '10px', fontWeight: 600 }}>
                  {accountBalances.length}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => setCurrencyDisplay(curr => curr === 'USD' ? 'INR' : 'USD')}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    padding: '4px 10px',
                    color: '#888',
                    fontSize: '10px',
                    fontWeight: 600,
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255,255,255,0.1)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255,255,255,0.06)'}
                >
                  {currencyDisplay}
                </button>
                <button
                  onClick={fetchAccountBalances}
                  disabled={loadingBalances}
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', cursor: loadingBalances ? 'not-allowed' : 'pointer', opacity: loadingBalances ? 0.5 : 1, padding: '4px 6px', display: 'flex', alignItems: 'center' }}
                >
                  <RefreshCw style={{ width: '11px', height: '11px', color: '#666' }} className={loadingBalances ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {loadingBalances ? (
              <div style={{ padding: '40px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                <RefreshCw style={{ color: '#facc15', width: '20px', height: '20px' }} className="animate-spin" />
                <span style={{ color: '#555', fontSize: '12px' }}>Loading…</span>
              </div>
            ) : accountBalances.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                <p style={{ color: '#555', fontSize: '12px' }}>No accounts found</p>
              </div>
            ) : (
              <div style={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', overflowX: 'visible', borderRadius: '0 0 20px 20px' }}>
                {/* Column labels */}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 16px 4px', position: 'sticky', top: 0, background: 'rgba(10,11,13,0.6)', backdropFilter: 'blur(8px)', zIndex: 1 }}>
                  <span style={{ color: '#333', fontSize: '9px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Trader</span>
                  <span style={{ color: '#333', fontSize: '9px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>Balance</span>
                </div>

                {[...accountBalances]
                  .sort((a, b) => {
                    const balA = currencyDisplay === 'USD' ? parseFloat(b.curr_balance || 0) : parseFloat(b.inr_balance || 0);
                    const balB = currencyDisplay === 'USD' ? parseFloat(a.curr_balance || 0) : parseFloat(a.inr_balance || 0);
                    return balA - balB;
                  })
                  .map((account, index, arr) => {
                    const bal = currencyDisplay === 'USD' ? parseFloat(account.curr_balance || 0) : parseFloat(account.inr_balance || 0);

                    const balColor = bal === 0 ? '#444'
                      : bal < 100  ? '#ff4d4d'
                      : bal < 300  ? '#ff9500'
                      : '#38FE60';

                    const glowColor = bal === 0 ? 'transparent'
                      : bal < 100  ? 'rgba(255,77,77,0.08)'
                      : bal < 300  ? 'rgba(255,149,0,0.08)'
                      : 'rgba(56,254,96,0.06)';

                    const avatarBg = bal === 0
                      ? 'linear-gradient(135deg,#1a1a1a,#222)'
                      : bal < 100
                        ? 'linear-gradient(135deg,rgba(255,77,77,0.25),rgba(255,77,77,0.1))'
                        : bal < 300
                          ? 'linear-gradient(135deg,rgba(255,149,0,0.25),rgba(255,149,0,0.1))'
                          : 'linear-gradient(135deg,rgba(56,254,96,0.22),rgba(56,254,96,0.08))';

                    const isHovered = hoveredBalanceIdx === index;

                    return (
                      <div
                        key={index}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '8px 16px',
                          borderBottom: index < arr.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none',
                          background: isHovered ? glowColor : 'transparent',
                          transition: 'background 0.2s',
                          cursor: 'default',
                          position: 'relative',
                        }}
                        onMouseEnter={() => setHoveredBalanceIdx(index)}
                        onMouseLeave={() => setHoveredBalanceIdx(null)}
                      >
                        {/* Rank */}
                        <span style={{ color: '#2a2a2a', fontSize: '10px', fontWeight: 800, width: '12px', flexShrink: 0, textAlign: 'center' }}>{index + 1}</span>

                        {/* Avatar */}
                        <div style={{
                          width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                          background: avatarBg,
                          border: `1px solid ${balColor === '#444' ? 'rgba(255,255,255,0.06)' : balColor + '33'}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          boxShadow: bal > 0 ? `0 0 10px ${balColor}22` : 'none',
                        }}>
                          <span style={{ fontWeight: 800, fontSize: '11px', color: balColor, textShadow: bal > 0 ? `0 0 8px ${balColor}88` : 'none' }}>
                            {account.name?.charAt(0)?.toUpperCase() || 'U'}
                          </span>
                        </div>

                        {/* Name only (no email inline) */}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <p style={{ color: '#e8e8e8', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: 1.35, margin: 0 }}>{account.name}</p>
                        </div>

                        {/* Balance pill */}
                        <div style={{
                          background: bal === 0 ? 'rgba(255,255,255,0.04)' : `${balColor}14`,
                          border: `1px solid ${bal === 0 ? 'rgba(255,255,255,0.06)' : balColor + '30'}`,
                          borderRadius: '20px', padding: '2px 9px', flexShrink: 0,
                        }}>
                          <span style={{ color: balColor, fontSize: '11px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em' }}>
                            {currencyDisplay === 'USD' ? '$' : '₹'}{bal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>

                        {/* Floating glass email tooltip — appears BELOW the row */}
                        {isHovered && account.email && (
                          <div style={{
                            position: 'absolute', left: '50%', top: 'calc(100% + 8px)',
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, rgba(28,32,38,0.97) 0%, rgba(18,20,24,0.97) 100%)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255,255,255,0.13)',
                            borderTop: '1px solid rgba(255,255,255,0.22)',
                            borderRadius: '10px',
                            padding: '7px 14px',
                            whiteSpace: 'nowrap',
                            zIndex: 100,
                            boxShadow: '0 12px 32px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.1)',
                            pointerEvents: 'none',
                            animation: 'tooltipFadeIn 0.15s ease',
                          }}>
                            {/* Arrow pointing UP */}
                            <div style={{
                              position: 'absolute', top: '-5px', left: '50%',
                              width: '8px', height: '8px',
                              background: 'rgba(28,32,38,0.97)',
                              border: '1px solid rgba(255,255,255,0.13)',
                              borderBottom: 'none', borderRight: 'none',
                              transform: 'translateX(-50%) rotate(45deg)',
                            }} />
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: balColor, boxShadow: `0 0 6px ${balColor}` }} />
                              <span style={{ color: '#d0d0d0', fontSize: '11px', fontWeight: 500, letterSpacing: '0.01em' }}>{account.email}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
              </div>
            )}

            {/* Bottom glass shine */}
            <div style={{ height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.06), transparent)' }} />
          </div>
        </div>

        {/* ── LIVE POSITIONS (right column, takes remaining space) ── */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-4">
            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#38FE60', boxShadow: '0 0 8px #38FE60' }} />
            <h2 className="text-lg font-bold text-white">Live Positions</h2>
            {loading && <RefreshCw style={{ color: '#38FE60' }} className="w-4 h-4 animate-spin" />}
          </div>

          {loading && users.length === 0 ? (
            <div style={cardStyle} className="p-14 flex flex-col items-center gap-4">
              <RefreshCw style={{ color: '#38FE60' }} className="w-10 h-10 animate-spin" />
              <p style={{ color: '#555' }} className="text-sm">Loading positions...</p>
            </div>
          ) : users.length === 0 ? (
            <div style={{ ...cardStyle, borderColor: 'rgba(255,255,255,0.06)' }} className="p-8 flex items-center gap-3">
              <AlertCircle style={{ color: '#555' }} className="w-5 h-5 flex-shrink-0" />
              <p style={{ color: '#666' }} className="text-sm">No active users found. Please add users with Delta Exchange API credentials.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {[...users]
                .sort((a, b) => {
                  const aPinned = pinnedUsers.includes(a.name);
                  const bPinned = pinnedUsers.includes(b.name);
                  if (aPinned && !bPinned) return -1;
                  if (!aPinned && bPinned) return 1;
                  return 0;
                })
                .map((user) => {
                const isUserPinned = pinnedUsers.includes(user.name);
                const toggleUserPin = () => {
                  setPinnedUsers(prev =>
                    prev.includes(user.name)
                      ? prev.filter(n => n !== user.name)
                      : [...prev, user.name]
                  );
                };
                return (
                <div key={user.id} style={{ ...cardStyle, borderLeft: isUserPinned ? '2px solid rgba(56,254,96,0.5)' : '2px solid transparent', transition: 'border 0.2s, box-shadow 0.2s', boxShadow: isUserPinned ? '0 8px 32px rgba(0,0,0,0.4), 0 0 0 1px rgba(56,254,96,0.2), inset 0 1px 0 rgba(255,255,255,0.08)' : '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)' }} className="overflow-hidden">
                  {/* User header row */}
                  <div className="flex items-center justify-between gap-4 px-5 py-4 flex-wrap" style={{ borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(255,255,255,0.02)' }}>
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {/* Avatar */}
                      <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: 'linear-gradient(135deg,#38FE60,#0ea868)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <span style={{ fontWeight: 800, fontSize: '17px', color: '#0a0b0d' }}>{user.name.charAt(0)}</span>
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white font-bold text-base">{user.name}</span>
                          {/* Status badge */}
                          <span style={{
                            fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', padding: '2px 8px', borderRadius: '20px',
                            background: user.status === 'active' ? 'rgba(56,254,96,0.12)' : user.status === 'error' ? 'rgba(255,77,77,0.12)' : 'rgba(255,255,255,0.06)',
                            color: user.status === 'active' ? '#38FE60' : user.status === 'error' ? '#ff4d4d' : '#666',
                            border: `1px solid ${user.status === 'active' ? 'rgba(56,254,96,0.2)' : user.status === 'error' ? 'rgba(255,77,77,0.2)' : 'rgba(255,255,255,0.08)'}`
                          }}>
                            {user.status.toUpperCase()}
                          </span>
                          {/* Position count badge */}
                          {user.positionCount > 0 && (
                            <span style={{ fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px', background: 'rgba(59,130,246,0.12)', color: '#60a5fa', border: '1px solid rgba(59,130,246,0.2)' }}>
                              {user.positionCount} {user.positionCount === 1 ? 'Position' : 'Positions'}
                            </span>
                          )}
                        </div>
                        <p style={{ color: '#888', fontSize: '12px' }} className="mt-0.5 truncate">{user.email}</p>
                      </div>
                    </div>

                    {/* Right side: PnL + pin + Open button */}
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <div className="text-right">
                        <p style={{ color: '#444', fontSize: '10px', letterSpacing: '0.1em' }} className="uppercase">Total P&L</p>
                        <p className={`text-2xl font-bold ${user.totalPnl >= 0 ? 'text-[#38FE60]' : 'text-[#ff4d4d]'}`}>
                          ${user.totalPnl.toFixed(2)}
                        </p>
                      </div>

                      {/* Pin button */}
                      <button
                        onClick={toggleUserPin}
                        title={isUserPinned ? 'Unpin' : 'Pin to top'}
                        style={{
                          background: isUserPinned ? 'rgba(56,254,96,0.1)' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${isUserPinned ? 'rgba(56,254,96,0.3)' : 'rgba(255,255,255,0.08)'}`,
                          borderRadius: '8px', cursor: 'pointer',
                          padding: '6px 8px', display: 'flex', alignItems: 'center',
                          color: isUserPinned ? '#38FE60' : '#555',
                          transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => {
                          e.currentTarget.style.background = isUserPinned ? 'rgba(255,77,77,0.1)' : 'rgba(56,254,96,0.1)';
                          e.currentTarget.style.color = isUserPinned ? '#ff4d4d' : '#38FE60';
                          e.currentTarget.style.borderColor = isUserPinned ? 'rgba(255,77,77,0.3)' : 'rgba(56,254,96,0.3)';
                        }}
                        onMouseLeave={e => {
                          e.currentTarget.style.background = isUserPinned ? 'rgba(56,254,96,0.1)' : 'rgba(255,255,255,0.04)';
                          e.currentTarget.style.color = isUserPinned ? '#38FE60' : '#555';
                          e.currentTarget.style.borderColor = isUserPinned ? 'rgba(56,254,96,0.3)' : 'rgba(255,255,255,0.08)';
                        }}
                      >
                        {isUserPinned
                          ? <PinOff style={{ width: '13px', height: '13px' }} />
                          : <Pin style={{ width: '13px', height: '13px' }} />
                        }
                      </button>

                      <button
                        onClick={() => {
                          setSelectedOpenUser(user);
                          setOpenFormData({ strikePrice: '', entryDate: '', expiry: '', optionType: 'C' });
                          setShowOpenUserDialog(true);
                        }}
                        style={{ background: '#38FE60', border: 'none', borderRadius: '10px', cursor: 'pointer', padding: '8px 14px', fontSize: '12px', fontWeight: 700, color: '#0a0b0d', display: 'flex', alignItems: 'center', gap: '6px' }}
                        className="hover:bg-[#2ee055] transition-colors whitespace-nowrap"
                      >
                        <TrendingUp className="w-3.5 h-3.5" />
                        Open Position
                      </button>
                    </div>
                  </div>

                  {/* Error banner */}
                  {user.error && (
                    <div style={{ background: 'rgba(255,77,77,0.08)', borderBottom: '1px solid rgba(255,77,77,0.15)', padding: '10px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <AlertCircle style={{ color: '#ff4d4d', flexShrink: 0 }} className="w-4 h-4" />
                      <span style={{ color: '#ff8080', fontSize: '13px' }}>{user.error}</span>
                    </div>
                  )}

                  {/* Positions area */}
                  <div className="px-5 py-3">
                    {user.positions.length === 0 ? (
                      <div className="flex flex-col items-center justify-center py-10 gap-3">
                        <Activity style={{ color: '#2a2a2a' }} className="w-12 h-12" />
                        <p style={{ color: '#333', fontSize: '13px' }}>No open positions</p>
                        {user.status === 'inactive' && !user.error && (
                          <p style={{ color: '#2a2a2a', fontSize: '11px' }}>User has no active positions</p>
                        )}
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                          <thead>
                            <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                              {['Symbol', 'Side', 'Size', 'Entry', 'Current', 'Growth %', 'UPL@Bid', 'Action'].map((h, i) => (
                                <th
                                  key={h}
                                  style={{ color: '#444', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', paddingBottom: '10px', paddingTop: '4px', textAlign: i > 1 ? (i === 7 ? 'center' : 'right') : 'left', paddingLeft: '8px', paddingRight: '8px' }}
                                  className="uppercase"
                                >
                                  {h}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {user.positions.map((position) => (
                              <tr
                                key={position.id}
                                style={{ borderBottom: '1px solid rgba(255,255,255,0.03)', transition: 'background 0.15s' }}
                                className="hover:bg-[rgba(255,255,255,0.02)]"
                              >
                                <td style={{ padding: '12px 10px' }}>
                                  <span className="text-white font-bold text-sm">{position.symbol}</span>
                                </td>
                                <td style={{ padding: '12px 10px' }}>
                                  <span style={{
                                    fontSize: '10px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px',
                                    background: position.side === 'long' ? 'rgba(56,254,96,0.12)' : 'rgba(255,77,77,0.12)',
                                    color: position.side === 'long' ? '#38FE60' : '#ff4d4d',
                                    border: `1px solid ${position.side === 'long' ? 'rgba(56,254,96,0.2)' : 'rgba(255,77,77,0.2)'}`
                                  }}>
                                    {position.side === 'long' ? 'LONG' : 'SHORT'}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 10px', textAlign: 'right', color: '#ccc', fontSize: '13px' }}>{position.size}</td>
                                <td style={{ padding: '12px 10px', textAlign: 'right', color: '#ccc', fontSize: '13px' }}>${position.entryPrice.toLocaleString()}</td>
                                <td style={{ padding: '12px 10px', textAlign: 'right', color: '#ccc', fontSize: '13px' }}>${position.currentPrice.toLocaleString()}</td>
                                <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: (position.percentageGrowth || 0) >= 0 ? '#38FE60' : '#ff4d4d' }}>
                                  {(position.percentageGrowth || 0) > 0 ? '+' : ''}{(position.percentageGrowth || 0).toFixed(2)}%
                                </td>
                                <td style={{ padding: '12px 10px', textAlign: 'right', fontSize: '13px', fontWeight: 600, color: (position.uplBid || 0) >= 0 ? '#38FE60' : '#ff4d4d' }}>
                                  ${(position.uplBid || 0).toFixed(2)}
                                </td>
                                <td style={{ padding: '12px 10px', textAlign: 'center' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedPosition({ ...position, userId: user.id, userName: user.name });
                                      setShowCloseDialog(true);
                                    }}
                                    style={{ background: 'rgba(255,77,77,0.1)', border: '1px solid rgba(255,77,77,0.25)', borderRadius: '8px', cursor: 'pointer', padding: '5px 12px', fontSize: '11px', fontWeight: 700, color: '#ff4d4d', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                    className="hover:bg-[rgba(255,77,77,0.2)] transition-colors"
                                  >
                                    <X className="w-3 h-3" />
                                    Close
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {/* close two-column wrapper */}
        </div>

        {/* ── DEBUG PANEL ── */}
        {showDebug && (
          <div style={{ background: 'linear-gradient(145deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.1)', borderTop: '1px solid rgba(255,255,255,0.18)', borderRadius: '18px', boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.08)', marginTop: '32px', overflow: 'hidden' }}>
            <div style={{ borderBottom: '1px solid rgba(250,204,21,0.1)', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ color: '#facc15', fontSize: '13px', fontWeight: 700, letterSpacing: '0.1em' }} className="uppercase">Debug Information</span>
            </div>
            <div className="p-5 space-y-5">
              {/* Errors */}
              {debugInfo.errors.length > 0 && (
                <div>
                  <p style={{ color: '#ff4d4d', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }} className="uppercase">Errors</p>
                  <div style={{ background: 'rgba(255,77,77,0.06)', border: '1px solid rgba(255,77,77,0.15)', borderRadius: '10px', padding: '12px' }}>
                    {debugInfo.errors.map((err, i) => (
                      <div key={i} style={{ color: '#ff8080', fontSize: '12px', fontFamily: 'monospace' }}>{err}</div>
                    ))}
                  </div>
                </div>
              )}

              {/* Raw response */}
              <div>
                <p style={{ color: '#60a5fa', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }} className="uppercase">
                  Raw Webhook Response ({debugInfo.rawResponse?.length || 0} chars)
                </p>
                <div style={{ background: '#060708', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '12px', maxHeight: '200px', overflow: 'auto' }}>
                  <pre style={{ color: '#888', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', margin: 0 }}>
                    {debugInfo.rawResponse || 'No response yet...'}
                  </pre>
                </div>
              </div>

              {/* Parsed data */}
              <div>
                <p style={{ color: '#38FE60', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }} className="uppercase">
                  Parsed Data (Array Length: {debugInfo.dataLength})
                </p>
                <div style={{ background: '#060708', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '12px', maxHeight: '200px', overflow: 'auto' }}>
                  <pre style={{ color: '#888', fontSize: '11px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', margin: 0 }}>
                    {JSON.stringify(debugInfo.parsedData, null, 2) || 'No data parsed yet...'}
                  </pre>
                </div>
              </div>

              {/* Processed users */}
              <div>
                <p style={{ color: '#c084fc', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }} className="uppercase">
                  Processed Users ({debugInfo.processedUsers.length})
                </p>
                <div style={{ background: '#060708', border: '1px solid rgba(255,255,255,0.05)', borderRadius: '10px', padding: '12px', maxHeight: '200px', overflow: 'auto' }}>
                  {debugInfo.processedUsers.length === 0 ? (
                    <p style={{ color: '#333', fontSize: '12px', margin: 0 }}>No users processed yet...</p>
                  ) : (
                    <div className="space-y-2">
                      {debugInfo.processedUsers.map((u, i) => (
                        <div key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: '8px' }}>
                          <div className="flex items-center justify-between">
                            <span style={{ color: '#ddd', fontSize: '12px', fontWeight: 600 }}>{u.name}</span>
                            <span style={{
                              fontSize: '10px', fontWeight: 700, padding: '2px 8px', borderRadius: '20px',
                              background: u.status === 'active' ? 'rgba(56,254,96,0.1)' : u.status === 'error' ? 'rgba(255,77,77,0.1)' : 'rgba(255,255,255,0.05)',
                              color: u.status === 'active' ? '#38FE60' : u.status === 'error' ? '#ff4d4d' : '#666'
                            }}>
                              {u.status}
                            </span>
                          </div>
                          <div style={{ color: '#555', fontSize: '11px', fontFamily: 'monospace', marginTop: '4px' }}>
                            Positions: {u.positionCount} | P&L: ${u.totalPnl?.toFixed(2) || '0.00'}
                            {u.error && <span style={{ color: '#ff6666', marginLeft: '8px' }}>Error: {u.error}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Current users state */}
              <div>
                <p style={{ color: '#fb923c', fontSize: '11px', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }} className="uppercase">
                  Current Users State ({users.length} users)
                </p>
                <div className="flex flex-wrap gap-2">
                  {users.map((u, i) => (
                    <span key={i} style={{ background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: '8px', padding: '3px 10px', fontSize: '11px', color: '#60a5fa' }}>
                      {u.name} ({u.positionCount} pos)
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════
          DIALOGS
      ══════════════════════════════════════════ */}

      {/* ── Close All Dialog ── */}
      <AlertDialog open={showCloseAllDialog} onOpenChange={setShowCloseAllDialog}>
        <AlertDialogContent style={{ background: '#111214', border: '1px solid rgba(255,77,77,0.2)', borderRadius: '20px', maxWidth: '480px' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-3">
              <div style={{ background: 'rgba(255,77,77,0.12)', borderRadius: '10px', padding: '8px' }}>
                <X style={{ color: '#ff4d4d' }} className="w-5 h-5" />
              </div>
              Close All Positions
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#666' }}>
              Are you sure you want to close ALL open positions for ALL users? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div style={{ background: '#0a0b0d', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px', margin: '8px 0' }} className="space-y-3">
            {[
              { label: 'Total Users', value: users.length, color: '#ccc' },
              { label: 'Users with Positions', value: users.filter(u => u.positions && u.positions.length > 0).length, color: '#ccc' },
              { label: 'Total Positions', value: users.reduce((sum, u) => sum + (u.positions?.length || 0), 0), color: '#facc15' },
              { label: 'Total P&L (UPL@Bid)', value: `$${users.reduce((sum, u) => sum + (u.totalPnl || 0), 0).toFixed(2)}`, color: users.reduce((sum, u) => sum + (u.totalPnl || 0), 0) >= 0 ? '#38FE60' : '#ff4d4d' },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: '8px' }}>
                <span style={{ color: '#555', fontSize: '13px' }}>{label}</span>
                <span style={{ color, fontWeight: 700, fontSize: '13px' }}>{value}</span>
              </div>
            ))}
          </div>

          <div style={{ background: 'rgba(255,77,77,0.06)', border: '1px solid rgba(255,77,77,0.15)', borderRadius: '10px', padding: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <AlertCircle style={{ color: '#ff4d4d', flexShrink: 0, marginTop: '1px' }} className="w-4 h-4" />
            <p style={{ color: '#ff8080', fontSize: '12px' }}>
              <strong>Warning:</strong> This will send close requests for all {users.reduce((sum, u) => sum + (u.positions?.length || 0), 0)} open positions across {users.filter(u => u.positions && u.positions.length > 0).length} users to the n8n webhook.
            </p>
          </div>

          <AlertDialogFooter className="gap-2 mt-4">
            <AlertDialogCancel style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ccc', borderRadius: '10px' }} className="hover:bg-[rgba(255,255,255,0.08)]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={closeAllPositions}
              disabled={closingAllPositions}
              style={{ background: '#ff4d4d', color: '#fff', fontWeight: 700, borderRadius: '10px', opacity: closingAllPositions ? 0.7 : 1 }}
              className="flex items-center gap-2 hover:bg-[#e03e3e]"
            >
              {closingAllPositions ? <><RefreshCw className="w-4 h-4 animate-spin" />Closing...</> : <><X className="w-4 h-4" />Close All Positions</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Open All Dialog ── */}
      <AlertDialog open={showOpenAllDialog} onOpenChange={setShowOpenAllDialog}>
        <AlertDialogContent style={{ background: '#111214', border: '1px solid rgba(56,254,96,0.15)', borderRadius: '20px', maxWidth: '480px' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-3">
              <div style={{ background: 'rgba(56,254,96,0.1)', borderRadius: '10px', padding: '8px' }}>
                <TrendingUp style={{ color: '#38FE60' }} className="w-5 h-5" />
              </div>
              Open All Positions
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#666' }}>
              Enter the parameters to open positions for all {users.length} users.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div style={{ background: '#0a0b0d', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px', margin: '8px 0' }} className="space-y-4">
            <div>
              <label className={formLabelCls}>Strike Price</label>
              <input type="number" placeholder="Enter strike price" step="0.01" value={openFormData.strikePrice} onChange={(e) => setOpenFormData({ ...openFormData, strikePrice: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={formLabelCls}>Entry Date / Time</label>
              <input type="text" placeholder="e.g., 2024-03-31 or 2024-03-31 15:30:00" value={openFormData.entryDate} onChange={(e) => setOpenFormData({ ...openFormData, entryDate: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={formLabelCls}>Expiry Date / Time</label>
              <input type="text" placeholder="e.g., 2024-03-31 or 2024-03-31 15:30:00" value={openFormData.expiry} onChange={(e) => setOpenFormData({ ...openFormData, expiry: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={formLabelCls}>Option Type</label>
              <select value={openFormData.optionType} onChange={(e) => setOpenFormData({ ...openFormData, optionType: e.target.value })} className={selectCls}>
                <option value="C">Call (C)</option>
                <option value="P">Put (P)</option>
              </select>
            </div>
          </div>

          <div style={{ background: 'rgba(56,254,96,0.05)', border: '1px solid rgba(56,254,96,0.12)', borderRadius: '10px', padding: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <AlertCircle style={{ color: '#38FE60', flexShrink: 0, marginTop: '1px' }} className="w-4 h-4" />
            <p style={{ color: '#6ee88a', fontSize: '12px' }}>
              <strong>Info:</strong> This will send an open positions request with the specified parameters to all {users.length} users via the n8n webhook.
            </p>
          </div>

          <AlertDialogFooter className="gap-2 mt-4">
            <AlertDialogCancel style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ccc', borderRadius: '10px' }} className="hover:bg-[rgba(255,255,255,0.08)]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={openAllPositions}
              disabled={openingAllPositions || !openFormData.strikePrice || !openFormData.entryDate || !openFormData.expiry}
              style={{ background: '#38FE60', color: '#0a0b0d', fontWeight: 700, borderRadius: '10px', opacity: (openingAllPositions || !openFormData.strikePrice || !openFormData.entryDate || !openFormData.expiry) ? 0.5 : 1 }}
              className="flex items-center gap-2 hover:bg-[#2ee055]"
            >
              {openingAllPositions ? <><RefreshCw className="w-4 h-4 animate-spin" />Opening...</> : <><TrendingUp className="w-4 h-4" />Open All Positions</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Open User Dialog ── */}
      <AlertDialog open={showOpenUserDialog} onOpenChange={setShowOpenUserDialog}>
        <AlertDialogContent style={{ background: '#111214', border: '1px solid rgba(56,254,96,0.15)', borderRadius: '20px', maxWidth: '480px' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-3">
              <div style={{ background: 'rgba(56,254,96,0.1)', borderRadius: '10px', padding: '8px' }}>
                <TrendingUp style={{ color: '#38FE60' }} className="w-5 h-5" />
              </div>
              Open Position
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#666' }}>
              {selectedOpenUser ? `Open a position for ${selectedOpenUser.name}.` : 'Select a user to open a position.'}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div style={{ background: '#0a0b0d', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px', margin: '8px 0' }} className="space-y-4">
            <div>
              <label className={formLabelCls}>Strike Price</label>
              <input type="number" placeholder="Enter strike price" step="0.01" value={openFormData.strikePrice} onChange={(e) => setOpenFormData({ ...openFormData, strikePrice: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={formLabelCls}>Entry Date / Time</label>
              <input type="text" placeholder="e.g., 2024-03-31 or 2024-03-31 15:30:00" value={openFormData.entryDate} onChange={(e) => setOpenFormData({ ...openFormData, entryDate: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={formLabelCls}>Expiry Date / Time</label>
              <input type="text" placeholder="e.g., 2024-03-31 or 2024-03-31 15:30:00" value={openFormData.expiry} onChange={(e) => setOpenFormData({ ...openFormData, expiry: e.target.value })} className={inputCls} />
            </div>
            <div>
              <label className={formLabelCls}>Option Type</label>
              <select value={openFormData.optionType} onChange={(e) => setOpenFormData({ ...openFormData, optionType: e.target.value })} className={selectCls}>
                <option value="C">Call (C)</option>
                <option value="P">Put (P)</option>
              </select>
            </div>
          </div>

          <div style={{ background: 'rgba(56,254,96,0.05)', border: '1px solid rgba(56,254,96,0.12)', borderRadius: '10px', padding: '12px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <AlertCircle style={{ color: '#38FE60', flexShrink: 0, marginTop: '1px' }} className="w-4 h-4" />
            <p style={{ color: '#6ee88a', fontSize: '12px' }}>
              <strong>Info:</strong> This will send an open position request for {selectedOpenUser?.name || 'the selected user'} via the n8n webhook.
            </p>
          </div>

          <AlertDialogFooter className="gap-2 mt-4">
            <AlertDialogCancel style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ccc', borderRadius: '10px' }} className="hover:bg-[rgba(255,255,255,0.08)]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={openUserPosition}
              disabled={openingUserPosition || !selectedOpenUser || !openFormData.strikePrice || !openFormData.entryDate || !openFormData.expiry}
              style={{ background: '#38FE60', color: '#0a0b0d', fontWeight: 700, borderRadius: '10px', opacity: (openingUserPosition || !selectedOpenUser || !openFormData.strikePrice || !openFormData.entryDate || !openFormData.expiry) ? 0.5 : 1 }}
              className="flex items-center gap-2 hover:bg-[#2ee055]"
            >
              {openingUserPosition ? <><RefreshCw className="w-4 h-4 animate-spin" />Opening...</> : <><TrendingUp className="w-4 h-4" />Open Position</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* ── Close Single Position Dialog ── */}
      <AlertDialog open={showCloseDialog} onOpenChange={setShowCloseDialog}>
        <AlertDialogContent style={{ background: '#111214', border: '1px solid rgba(255,77,77,0.2)', borderRadius: '20px', maxWidth: '460px' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white flex items-center gap-3">
              <div style={{ background: 'rgba(255,77,77,0.12)', borderRadius: '10px', padding: '8px' }}>
                <X style={{ color: '#ff4d4d' }} className="w-5 h-5" />
              </div>
              Close Position
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: '#666' }}>
              Are you sure you want to close this position? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {selectedPosition && (
            <div style={{ background: '#0a0b0d', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '16px', margin: '8px 0' }} className="space-y-2.5">
              {[
                { label: 'User', value: selectedPosition.userName, color: '#ddd' },
                { label: 'Symbol', value: selectedPosition.symbol, color: '#ddd' },
                { label: 'Product ID', value: selectedPosition.productId, color: '#888', small: true },
                { label: 'Side', value: selectedPosition.side.toUpperCase(), color: selectedPosition.side === 'long' ? '#38FE60' : '#ff4d4d' },
                { label: 'Size', value: selectedPosition.size, color: '#ddd' },
                { label: 'UPL@Bid', value: `$${(selectedPosition.uplBid || 0).toFixed(2)}`, color: (selectedPosition.uplBid || 0) >= 0 ? '#38FE60' : '#ff4d4d' },
              ].map(({ label, value, color, small }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.04)', paddingBottom: '8px' }}>
                  <span style={{ color: '#555', fontSize: '13px' }}>{label}</span>
                  <span style={{ color, fontWeight: 700, fontSize: small ? '11px' : '13px' }}>{value}</span>
                </div>
              ))}

              {/* Webhook data check */}
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                <p style={{ color: '#facc15', fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', marginBottom: '8px' }} className="uppercase">Webhook Data Check</p>
                {(() => {
                  const u = users.find(u => u.id === selectedPosition.userId);
                  return u ? (
                    <div className="space-y-1.5">
                      {[
                        { label: 'API Key', present: !!u.apiKey, preview: u.apiKey },
                        { label: 'API Secret', present: !!u.apiSecret, preview: u.apiSecret },
                      ].map(({ label, present, preview }) => (
                        <div key={label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ color: '#555', fontSize: '11px' }}>{label}</span>
                          <span style={{ color: present ? '#38FE60' : '#ff4d4d', fontSize: '11px', fontFamily: 'monospace' }}>
                            {present ? `Present (${preview.substring(0, 15)}...)` : 'Missing'}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: '#ff4d4d', fontSize: '11px' }}>User not found</p>
                  );
                })()}
              </div>
            </div>
          )}

          <AlertDialogFooter className="gap-2 mt-4">
            <AlertDialogCancel style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#ccc', borderRadius: '10px' }} className="hover:bg-[rgba(255,255,255,0.08)]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedPosition && closePosition(selectedPosition, selectedPosition.userId)}
              disabled={closingPosition}
              style={{ background: '#ff4d4d', color: '#fff', fontWeight: 700, borderRadius: '10px', opacity: closingPosition ? 0.7 : 1 }}
              className="flex items-center gap-2 hover:bg-[#e03e3e]"
            >
              {closingPosition ? <><RefreshCw className="w-4 h-4 animate-spin" />Closing...</> : <><X className="w-4 h-4" />Close Position</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default SuperAdmin;
